import React, { Suspense, useMemo } from 'react';
import { useSelector } from 'react-redux';

import useIsMobile from 'hooks/useIsMobile';
import { isNLPLoadingSelector, promptSelector } from 'store/reducers/nlp/nlp-reducer';
import { showingLoadingScreenSelector } from 'store/reducers/people-steps-reducer/people-steps-reducer';
import {
  currentStepSelector,
  isPeopleLoadingSelector,
  previousFiltersLengthSelector,
} from 'store/reducers/people/people-reducer';
import { ThemeProvider } from 'styled-components';

import PeopleSearch from '../PeopleContent/PeopleSearch';
import PeopleMobileSwitcher from '../PeopleMobileSwitcher';
import PeopleStepsOneExamples from './PeopleStepsOne/PeopleStepsOneExamples';
import PeopleStepsOneLoading from './PeopleStepsOne/PeopleStepsOneLoading';
import PeopleStepsOneSpotlight from './PeopleStepsOne/PeopleStepsOneSpotlight';
import {
  PeopleStepsOneDescription,
  PeopleStepsOneBox,
  PeopleStepsOneLeftContainer,
  PeopleStepsOneContainer,
  PeopleStepsOneTitle,
  PeopleStepsOneWrapper,
} from './PeopleStepsOne/styled';
import { PeopleStepsWrapper } from './styled';

const PeopleStepsTwo = React.lazy(() => import('./PeopleStepsTwo'));
const PeopleStepsThree = React.lazy(() => import('./PeopleStepsThree'));
const PeopleStepsFour = React.lazy(() => import('./PeopleStepsFour'));

export default function PeopleSteps() {
  const isMobile = useIsMobile();
  const currentStep = useSelector(currentStepSelector);
  const isNlpLoading = useSelector(isNLPLoadingSelector);
  const prompt = useSelector(promptSelector);
  const filters = useSelector(previousFiltersLengthSelector);
  const isPeopleLoading = useSelector(isPeopleLoadingSelector);
  const showingLoadingScreen = useSelector(showingLoadingScreenSelector);

  const isLoading = useMemo<boolean>(() => {
    return isNlpLoading || (isPeopleLoading && filters > 0);
  }, [isNlpLoading, isPeopleLoading, filters]);

  const showLoadingScreen = useMemo<boolean>(() => {
    return isNlpLoading || showingLoadingScreen;
  }, [isNlpLoading, isPeopleLoading, filters, showingLoadingScreen]);

  const isPromptTransition = useMemo<boolean>(() => {
    return currentStep === 1 && Boolean(prompt);
  }, [currentStep, prompt]);

  const shouldShowLoadingScreen = useMemo<boolean>(() => {
    return showLoadingScreen || isPromptTransition;
  }, [showLoadingScreen, isPromptTransition]);

  const shouldShowStepOneContent = useMemo<boolean>(() => {
    return currentStep === 1 && !isLoading && !shouldShowLoadingScreen;
  }, [currentStep, isLoading, shouldShowLoadingScreen]);

  return (
    <ThemeProvider
      theme={{
        currentStep,
        isLoading: shouldShowLoadingScreen,
        isGettingInfo: shouldShowStepOneContent,
      }}
    >
      <PeopleStepsWrapper>
        {isMobile ? <PeopleMobileSwitcher /> : null}
        <PeopleStepsOneWrapper>
          <PeopleStepsOneContainer>
            <PeopleStepsOneLeftContainer>
              {shouldShowStepOneContent ? (
                <>
                  {!isMobile ? <PeopleStepsOneBox /> : null}
                  <PeopleStepsOneTitle>Who can we connect you with?</PeopleStepsOneTitle>
                  <PeopleStepsOneDescription>
                    Describe who you're looking for in everyday language – we’ll connect the dots in
                    seconds across the member companies.
                  </PeopleStepsOneDescription>
                </>
              ) : null}
              <PeopleSearch />
              {shouldShowLoadingScreen ? <PeopleStepsOneLoading /> : null}
            </PeopleStepsOneLeftContainer>
            {shouldShowStepOneContent ? <PeopleStepsOneSpotlight /> : null}
          </PeopleStepsOneContainer>
          {shouldShowStepOneContent ? <PeopleStepsOneExamples /> : null}
        </PeopleStepsOneWrapper>
        <Suspense fallback={<></>}>
          {currentStep === 2 && !showingLoadingScreen ? <PeopleStepsTwo /> : null}
          {currentStep === 3 && !showingLoadingScreen ? <PeopleStepsThree /> : null}
          {currentStep === 4 && !showingLoadingScreen ? <PeopleStepsFour /> : null}
        </Suspense>
      </PeopleStepsWrapper>
    </ThemeProvider>
  );
}
